import React from "react";
import { COLOR_SYSTEM } from "components/design/design-system";
import useRipple from "components/hooks/useRipple";
import Icon from "components/atoms/Icon";
import {
  IconButtonWrapper,
  IconWithTextButtonWrapper,
  TextButtonWrapper,
} from "components/atoms/Button/style";

export type ButtonSize = "Large" | "Medium" | "Small" | "XSmall";
export type ButtonType = "Icon" | "Text" | "IconWithText";
export type ButtonVariant = "Contained" | "Outlined" | "Tinted" | "Ghost";
export type ButtonUsage = "Primary" | "Secondary" | "Tertiary" | "Quaternary" | "Negative";

export interface ButtonProps {
  /** 버튼의 이용 목적 */
  buttonType?: "button" | "submit" | "reset";
  /** 버튼 크기 */
  size: ButtonSize;
  /** 버튼 타입 */
  type: ButtonType;
  /** 버튼 종류 */
  variant: ButtonVariant;
  /** 버튼 색상 */
  color: ButtonUsage;
  /** 버튼 안에 들어가는 content */
  children?: React.ReactNode | string;
  /** 버튼 활성 여부 */
  disabled?: boolean;
  /** 버튼 비활성일때 색상 변경 여부 */
  disabledColor?: boolean;
  /** 아이콘 위치 여부(택스트 앞) */
  left?: boolean;
  /** 아이콘 위치 여부(택스트 뒤) */
  right?: boolean;
  /** 아이콘 */
  icon?: string;
  /** 아이콘 색상 */
  iconColor?: string;
  /** 아이콘 크기 */
  iconSize?: number;
  /** 커스텀 class */
  className?: string;
  /** 커스텀 inline CSS */
  style?: React.CSSProperties;
  /** 클릭 이벤트 핸들러 */
  onClick?: (params?: any) => any;
  onBlur?: (params?: any) => any;
  /** 마우스 hover 시 표시되는 툴팁 텍스트 */
  title?: string;
  /** data-* 속성들 */
  [key: `data-${string}`]: any;
}

const Button = ({
  buttonType = "button",
  color,
  size,
  variant,
  children,
  disabled = false,
  disabledColor = false,
  left,
  right,
  icon,
  iconColor,
  iconSize,
  type,
  className,
  style,
  onClick = () => {},
  onBlur = () => {},
  title,
  ...dataProps
}: ButtonProps) => {
  const [coords, isRippling, diameter, handleRippleClick] = useRipple({
    x: -1,
    y: -1,
  });

  const iconSizeMap = {
    Large: 24,
    Medium: 20,
    Small: 16,
    XSmall: 12,
  };

  const iconColorMap = {
    Contained: COLOR_SYSTEM.get("Gray")?.[0],
    Primary: COLOR_SYSTEM.get("Blue")?.[400],
    Secondary: COLOR_SYSTEM.get("Gray")?.[700],
    Tertiary: COLOR_SYSTEM.get("Gray")?.[400],
    Quaternary: COLOR_SYSTEM.get("Gray")?.[200],
    Negative: COLOR_SYSTEM.get("Red")?.[300],
  };

  const renderByType = () => {
    switch (type) {
      case "Icon":
        return (
          <IconButtonWrapper
            type={buttonType}
            tabIndex={-1}
            color={color}
            size={size}
            variant={variant}
            disabled={disabled}
            disabledColor={disabledColor}
            className={className}
            style={style}
            onClick={onClick}
            onMouseDown={(e) => {
              handleRippleClick(e);
              e.preventDefault();
            }}
            onBlur={onBlur}
            title={title}
            {...dataProps}
          >
            {icon && (
              <Icon
                name={icon}
                color={variant === "Contained" ? iconColorMap[variant] : iconColorMap[color]}
                size={iconSize || iconSizeMap[size]}
              />
            )}
            {isRippling && (
              <span
                className="ripple"
                style={{
                  left: coords.x,
                  top: coords.y,
                  width: diameter,
                  height: diameter,
                }}
              />
            )}
          </IconButtonWrapper>
        );
      case "Text":
        return (
          <TextButtonWrapper
            type={buttonType}
            color={color}
            size={size}
            variant={variant}
            disabled={disabled}
            disabledColor={disabledColor}
            className={className}
            style={style}
            onClick={onClick}
            onBlur={onBlur}
            onMouseDown={(e) => {
              handleRippleClick(e);
              e.preventDefault();
            }}
            title={title}
            {...dataProps}
          >
            {children}
            {isRippling && (
              <span
                className="ripple"
                style={{
                  left: coords.x,
                  top: coords.y,
                  width: diameter,
                  height: diameter,
                }}
              />
            )}
          </TextButtonWrapper>
        );
      case "IconWithText":
        return (
          <IconWithTextButtonWrapper
            type={buttonType}
            color={color}
            size={size}
            variant={variant}
            disabled={disabled}
            disabledColor={disabledColor}
            left={left}
            right={right}
            className={className}
            style={style}
            onClick={onClick}
            onBlur={onBlur}
            onMouseDown={(e) => {
              handleRippleClick(e);
              e.preventDefault();
            }}
            title={title}
            {...dataProps}
          >
            {left && icon && (
              <Icon
                name={icon}
                color={variant === "Contained" ? iconColorMap[variant] : iconColorMap[color]}
                size={iconSize || iconSizeMap[size]}
              />
            )}
            {children}
            {right && icon && (
              <Icon
                name={icon}
                color={variant === "Contained" ? iconColorMap[variant] : iconColorMap[color]}
                size={iconSize || iconSizeMap[size]}
              />
            )}
            {isRippling && (
              <span
                className="ripple"
                style={{
                  left: coords.x,
                  top: coords.y,
                  width: diameter,
                  height: diameter,
                }}
              />
            )}
          </IconWithTextButtonWrapper>
        );
      default:
        return null;
    }
  };

  return <>{renderByType()}</>;
};

export default Button;
